import { useState } from 'react';
import { useAppCatalogLocale } from '@/i18n/context';
import { formatCopy, useNamespaceCopy } from '@/i18n/ns';
import storefrontCopy, { type StorefrontCopyKey } from '@/i18n/ns/storefront';
import { useStorefrontApi, type KnowledgeSource, type StoreNote } from './api';
import { StorefrontDesignPreview } from './design-preview';
import { creditsPhrase, when } from './format';
import {
  CHECKS_SCOPE,
  checksReachCart,
  isStoreReadTool,
  READ_MINUTES,
  READ_SCOPE,
  READS_CART,
  storeRead,
  type StoreRead,
} from './read-request';
import { useRemote } from './use-storefront';

type Args = Record<string, unknown>;

/** Which Storefront approval this is: its design, the notes it shares with shoppers, or a paid read. */
export function storefrontChangeKind(tool: string): 'design' | 'knowledge' | 'read' | null {
  if (tool === 'change_storefront_design') return 'design';
  if (tool === 'change_storefront_knowledge') return 'knowledge';
  if (isStoreReadTool(tool)) return 'read';
  return null;
}

const GROUPS: [string, StorefrontCopyKey][] = [
  ['colors', 'designGroupColors'],
  ['type', 'designGroupType'],
  ['shape', 'designGroupShape'],
  ['launcher', 'designGroupLauncher'],
  ['panel', 'designGroupPanel'],
  ['products', 'designGroupProducts'],
  ['cart', 'designGroupCart'],
  ['copy', 'designGroupCopy'],
  ['translations', 'designGroupTranslations'],
  ['logo', 'designGroupLogo'],
  ['css', 'designGroupCss'],
];
const COLORS: Record<string, StorefrontCopyKey> = {
  brand: 'designColorBrand',
  actions: 'designColorActions',
  panel: 'designColorPanel',
  surface: 'designColorSurface',
  ink: 'designColorInk',
  muted: 'designColorMuted',
  line: 'designColorLine',
  bubble: 'designColorBubble',
  bubbleInk: 'designColorBubbleInk',
  header: 'designColorHeader',
  headerInk: 'designColorHeaderInk',
};
const WORDS: Record<string, StorefrontCopyKey> = {
  assistantName: 'designWordsName',
  greeting: 'designWordsGreeting',
  starters: 'designWordsStarters',
  launcherLabel: 'designWordsLauncher',
  placeholder: 'designWordsPlaceholder',
  aiLabel: 'designWordsAi',
  personLabel: 'designWordsPerson',
};

const record = (value: unknown): Args | null =>
  value && typeof value === 'object' && !Array.isArray(value) ? (value as Args) : null;

/** A Storefront change in the approval canvas: what it makes, never its raw fields. */
export function StorefrontActionDraft({
  tool,
  args,
  status,
  result,
}: {
  tool: string;
  args: Args;
  /** The approval's status and result, for a read that has started. */
  status?: string;
  result?: unknown;
}) {
  const kind = storefrontChangeKind(tool);
  const read = storeRead(tool, args);
  if (kind === 'read')
    return read ? <ReadRequest read={read} args={args} status={status} result={result} /> : null;
  const storeId = typeof args.store_id === 'string' ? args.store_id : null;
  const note = typeof args.note === 'string' && args.note.trim() ? args.note.trim() : null;
  if (!kind || !storeId) return null;
  return (
    <div className="sf-action-draft">
      {note && (
        <p className="sf-action-note" data-testid="sf-action-note">
          {note}
        </p>
      )}
      {kind === 'design' ? (
        <DesignChange storeId={storeId} changes={record(args.changes) ?? {}} />
      ) : (
        <KnowledgeChange storeId={storeId} args={args} />
      )}
    </div>
  );
}

function DesignChange({ storeId, changes }: { storeId: string; changes: Args }) {
  const copy = useNamespaceCopy(storefrontCopy);
  const groups = GROUPS.filter(([key]) => Object.hasOwn(changes, key));
  const colors = Object.entries(record(changes.colors) ?? {}).filter(([key]) => COLORS[key]);
  const words = Object.entries(record(changes.copy) ?? {}).filter(([key]) => WORDS[key]);
  return (
    <>
      <StorefrontDesignPreview storeId={storeId} changes={changes} />
      {groups.length > 0 && (
        <section className="sf-change" aria-label={copy.designChanges}>
          <h4>{copy.designChanges}</h4>
          <ul className="sf-change-groups">
            {groups.map(([key, label]) => (
              <li key={key} data-testid="sf-change-group">
                {copy[label]}
                {changes[key] === null && <span> · {copy.designReset}</span>}
              </li>
            ))}
          </ul>
          {colors.length > 0 && (
            <ul className="sf-change-swatches">
              {colors.map(([key, value]) => (
                <li key={key}>
                  {typeof value === 'string' && value.startsWith('#') ? (
                    // The store's own colour, shown as itself.
                    <span className="sf-swatch" style={{ background: value }} aria-hidden="true" />
                  ) : (
                    <span className="sf-swatch" data-empty="true" aria-hidden="true" />
                  )}
                  <span className="sf-swatch-label">{copy[COLORS[key]]}</span>
                  <span className="sf-swatch-value" data-testid="sf-swatch-value">
                    {value === null
                      ? copy.designReset
                      : value === 'brand'
                        ? copy.designActionsBrand
                        : value === 'ink'
                          ? copy.designActionsInk
                          : String(value).toUpperCase()}
                  </span>
                </li>
              ))}
            </ul>
          )}
          {words.length > 0 && (
            <dl className="sf-change-words">
              {words.map(([key, value]) => (
                <div key={key}>
                  <dt>{copy[WORDS[key]]}</dt>
                  <dd>
                    {value === null ? (
                      copy.designReset
                    ) : Array.isArray(value) ? (
                      <ul>
                        {value.map((item, index) => (
                          <li key={index}>{String(item)}</li>
                        ))}
                      </ul>
                    ) : (
                      String(value)
                    )}
                  </dd>
                </div>
              ))}
            </dl>
          )}
        </section>
      )}
    </>
  );
}

/**
 * A paid read in words: the shop, what it reads, how long it usually takes, that it uses company
 * credits and what it saves, then what Chat asked the read to fix, quoted as Chat wrote it.
 */
function ReadRequest({
  read,
  args,
  status,
  result,
}: {
  read: StoreRead;
  args: Args;
  status?: string;
  result?: unknown;
}) {
  const copy = useNamespaceCopy(storefrontCopy);
  const locale = useAppCatalogLocale() ?? 'en';
  const api = useStorefrontApi();
  const [whole, setWhole] = useState(false);
  const storeId = typeof args.store_id === 'string' ? args.store_id : null;
  // Read again once approved, so a started read shows its own run, not the one before it.
  const remote = useRemote(
    storeId ? () => api.get(storeId).then((answer) => answer.store) : null,
    `${storeId ?? ''}:${status ?? ''}`,
  );
  const store = remote.data;
  // A new shop is named by the address Chat will read, exactly; a saved one by its name and domain
  // (said once while the store is still named by its domain).
  const shop = store
    ? store.name === store.domain
      ? store.domain
      : `${store.name} · ${store.domain}`
    : typeof args.url === 'string'
      ? args.url
      : null;
  const code = typeof args.market === 'string' ? args.market : '';
  const market = store?.markets?.find((entry) => entry.code === code);
  const focus = typeof args.focus === 'string' && args.focus.trim() ? args.focus.trim() : null;
  // Once approved, the read runs on the Storefront page: its Setup tab follows it. A new shop's
  // id comes back in the result.
  // A start for a shop the company already has started nothing (src/storefront/read-tools.mjs).
  const existing = status === 'executed' && record(result)?.started === false;
  const started = status === 'executed' && !existing;
  const startedId = storeId ?? record(record(result)?.store)?.id;
  const setupHref =
    (started || existing) && typeof startedId === 'string'
      ? `#/workspace/storefront?store=${encodeURIComponent(startedId)}&tab=setup`
      : null;
  // Checking the saved work again has no duration the server confirms: no time is promised.
  const minutes = read === 'checks' ? null : READ_MINUTES[read];
  // No cap and no estimate we cannot stand behind: what this shop's last read used, in credits.
  const lastRead = !started ? store?.run?.costCreditMicros : null;
  // The last read's spend is history, never the price of checking again.
  const cost =
    read === 'checks'
      ? copy.checksCost
      : typeof lastRead === 'number' && lastRead > 0
        ? formatCopy(
            store?.run?.costStatus === 'partial' ? copy.readCostLastPartial : copy.readCostLast,
            {
              credits: creditsPhrase(lastRead, locale, copy),
            },
          )
        : copy.readCost;
  // The run's start time only while it runs: an older approval never borrows a later read's time.
  const running = Boolean(
    store?.run && ['running', 'checking', 'queued', 'starting'].includes(store.run.status),
  );
  const startedAt = started && running ? store?.run?.startedAt : null;
  // Checking a read of one part again tries only that part, as Setup says while it runs.
  const scope = read === 'checks' ? store?.run?.scope : null;
  const checked = scope ? CHECKS_SCOPE[scope] : undefined;
  const facts: [StorefrontCopyKey, string][] = [
    ...(shop ? [['readShopLabel', shop] as [StorefrontCopyKey, string]] : []),
    [
      read === 'checks' ? 'checksScopeLabel' : 'readScopeLabel',
      checked
        ? copy[checked]
        : formatCopy(copy[READ_SCOPE[read]], {
            market: market ? `${market.name} · ${market.site}` : code.toUpperCase(),
          }),
    ],
    ...(minutes
      ? [
          [
            'readTimeLabel',
            formatCopy(copy.readTime, { min: String(minutes[0]), max: String(minutes[1]) }),
          ] as [StorefrontCopyKey, string],
        ]
      : []),
    ['readCostLabel', cost],
    [
      'readAfterLabel',
      read === 'checks'
        ? store?.live
          ? copy.checksAfterLive
          : copy.checksAfter
        : read === 'store'
          ? copy.readAfterNew
          : store?.live
            ? copy.readAfterLive
            : copy.readAfter,
    ],
  ];
  return (
    <div className="sf-read" data-testid="sf-read">
      {existing && (
        <p className="sf-read-started" role="status" data-testid="sf-read-existing">
          {copy.readAlreadyHere}
          {setupHref && (
            <>
              {' '}
              <a href={setupHref} data-testid="sf-read-follow">
                {copy.readOpenShop}
              </a>
            </>
          )}
        </p>
      )}
      {started && (
        <p className="sf-read-started" role="status" data-testid="sf-read-started">
          {read === 'checks'
            ? copy.checksStarted
            : startedAt && minutes
              ? formatCopy(copy.readStartedAt, {
                  time: when(startedAt, locale),
                  min: String(minutes[0]),
                  max: String(minutes[1]),
                })
              : copy.readStarted}
          {setupHref && (
            <>
              {' '}
              <a href={setupHref} data-testid="sf-read-follow">
                {copy.readFollow}
              </a>
            </>
          )}
        </p>
      )}
      <dl className="sf-read-facts">
        {facts.map(([label, value]) => (
          <div key={label}>
            <dt>{copy[label]}</dt>
            <dd data-testid="sf-read-fact">{value}</dd>
          </div>
        ))}
      </dl>
      {/* A check's cart note waits for its store: only the read's own scope says the cart is tried. */}
      {READS_CART.has(read) && (read !== 'checks' || (store && checksReachCart(scope))) && (
        <p className="sf-action-hint">{copy.readCartNote}</p>
      )}
      {focus && (
        <section className="sf-read-focus" aria-label={copy.readFocusLabel}>
          <h4>{copy.readFocusLabel}</h4>
          {/* Chat's brief for the reader can run long: three lines, the rest on request. */}
          <blockquote
            data-testid="sf-read-focus"
            data-whole={whole || focus.length < 240 || undefined}
          >
            {focus}
          </blockquote>
          {focus.length >= 240 && (
            <button type="button" className="ws-text-button" onClick={() => setWhole(!whole)}>
              {whole ? copy.readFocusLess : copy.readFocusAll}
            </button>
          )}
        </section>
      )}
    </div>
  );
}

type NoteInput = { id?: string; title?: unknown; text?: unknown; source?: KnowledgeSource };

function KnowledgeChange({ storeId, args }: { storeId: string; args: Args }) {
  const copy = useNamespaceCopy(storefrontCopy);
  const api = useStorefrontApi();
  const remote = useRemote(() => api.get(storeId).then((answer) => answer.store), storeId);
  const current: StoreNote[] = remote.data?.knowledge?.notes ?? [];
  const set = (Array.isArray(args.set) ? args.set : []) as NoteInput[];
  const remove = (Array.isArray(args.remove) ? args.remove : []).filter(
    (id): id is string => typeof id === 'string',
  );
  // The lines about the shop as a whole: added as the team's own, taken out by id, or the written part cleared.
  const profile = record(args.profile) ?? {};
  const lines = (Array.isArray(profile.add) ? profile.add : []).filter(
    (text): text is string => typeof text === 'string',
  );
  const removedLines = (Array.isArray(profile.remove) ? profile.remove : []).filter(
    (id): id is string => typeof id === 'string',
  );
  const ours = remote.data?.knowledge?.profile?.additions ?? [];
  return (
    <>
      <ul className="sf-notes">
        {set.map((note, index) => (
          <li key={note.id ?? `new-${index}`} className="sf-note">
            <div className="sf-note-head">
              <b>{String(note.title ?? '')}</b>
              <span className="sf-note-tag" data-testid="sf-note-tag">
                {note.id ? copy.noteChanged : copy.noteNew}
              </span>
            </div>
            <p className="sf-note-text">{String(note.text ?? '')}</p>
            <NoteSource source={note.source} />
          </li>
        ))}
        {remove.map((id) => (
          <li key={id} className="sf-note" data-removed="true">
            <div className="sf-note-head">
              <b>{current.find((note) => note.id === id)?.title ?? id}</b>
              <span className="sf-note-tag" data-testid="sf-note-tag">
                {copy.noteRemoved}
              </span>
            </div>
          </li>
        ))}
        {lines.map((text, index) => (
          <li key={`line-${index}`} className="sf-note" data-testid="sf-profile-line">
            <div className="sf-note-head">
              <b>{copy.profileTeam}</b>
              <span className="sf-note-tag" data-testid="sf-note-tag">
                {copy.noteNew}
              </span>
            </div>
            <p className="sf-note-text">{text}</p>
          </li>
        ))}
        {removedLines.map((id) => (
          <li key={id} className="sf-note" data-removed="true" data-testid="sf-profile-line">
            <div className="sf-note-head">
              <b>{ours.find((line) => line.id === id)?.text ?? id}</b>
              <span className="sf-note-tag" data-testid="sf-note-tag">
                {copy.noteRemoved}
              </span>
            </div>
          </li>
        ))}
        {profile.clear === true && (
          <li className="sf-note" data-removed="true" data-testid="sf-profile-line">
            <div className="sf-note-head">
              <b>{copy.profileTitle}</b>
              <span className="sf-note-tag" data-testid="sf-note-tag">
                {copy.profileCleared}
              </span>
            </div>
          </li>
        )}
      </ul>
      <p className="sf-action-hint">{copy.knowledgeApprovalNote}</p>
    </>
  );
}

/** Where a note came from: a page (opens in a new tab), a Knowledge page, a company file or the chat. */
export function NoteSource({ source }: { source?: KnowledgeSource }) {
  const copy = useNamespaceCopy(storefrontCopy);
  if (!source) return null;
  if (source.kind === 'page') {
    let label = source.url;
    try {
      const url = new URL(source.url);
      label = `${url.host}${url.pathname === '/' ? '' : url.pathname}`;
    } catch {
      return null;
    }
    return (
      <p className="sf-note-source">
        <a href={source.url} target="_blank" rel="noopener noreferrer">
          {label}
        </a>
      </p>
    );
  }
  const text =
    source.kind === 'knowledge_page'
      ? formatCopy(copy.noteSourceKnowledge, { title: source.title })
      : source.kind === 'company_file'
        ? formatCopy(copy.noteSourceFile, { name: source.name })
        : copy.noteSourceChat;
  return <p className="sf-note-source">{text}</p>;
}
